import * as React from "react";

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { cn } from "@replohq/lattice/utils/cn";
import {
  Check,
  Copy,
  ExternalLink,
  Maximize2,
  Minimize2,
  X,
} from "lucide-react";

import { DEBUG_DEV_PANEL_BASE_CLASS } from "./debugPanelChrome";
import { getStripeDashboardUrl } from "./tabs/agent-billing/shared";

const DebugJsonNode: React.FC<{
  value: unknown;
  depth?: number;
  defaultExpanded?: boolean;
}> = ({ value, depth = 0, defaultExpanded = false }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  if (value === null) {
    return <span className="text-rose">null</span>;
  }
  if (value === undefined) {
    return <span className="text-rose">undefined</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-violet">{String(value)}</span>;
  }
  if (typeof value === "number") {
    return <span className="text-emerald">{value}</span>;
  }
  if (typeof value === "string") {
    const stripeUrl = getStripeDashboardUrl(value);
    if (stripeUrl) {
      return (
        <a
          href={stripeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline"
        >
          &quot;{value}&quot;
          <ExternalLink className="ml-0.5 inline size-2.5" />
        </a>
      );
    }
    return <span>&quot;{value}&quot;</span>;
  }

  const isArray = Array.isArray(value);
  const entries = isArray
    ? // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      (value as unknown[]).map((item, index) => [String(index), item] as const)
    : // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      Object.entries(value as Record<string, unknown>);
  const openBrace = isArray ? "[" : "{";
  const closeBrace = isArray ? "]" : "}";

  if (entries.length === 0) {
    return (
      <span>
        {openBrace}
        {closeBrace}
      </span>
    );
  }

  return (
    <span>
      <button
        type="button"
        className="cursor-pointer rounded px-0.5 hover:bg-amber/50"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded
          ? `▼ ${openBrace}`
          : `▶ ${openBrace}…${closeBrace} (${entries.length})`}
      </button>
      {expanded && (
        <div className="ml-3 border-l border-amber/30 pl-2">
          {entries.map(([entryKey, entryValue]) => (
            <div key={entryKey}>
              <span className="font-bold">{entryKey}:</span>{" "}
              <DebugJsonNode
                value={entryValue}
                depth={depth + 1}
                defaultExpanded={defaultExpanded}
              />
            </div>
          ))}
        </div>
      )}
      {expanded && <span>{closeBrace}</span>}
    </span>
  );
};

export const DebugOverlay: React.FC<{
  data: Record<string, unknown>;
  isVisible: boolean;
  referenceRef: React.RefObject<HTMLElement | null>;
}> = ({ data, isVisible, referenceRef }) => {
  const [closed, setClosed] = React.useState(false);
  const [expandAll, setExpandAll] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: "right-start",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    elements: { reference: referenceRef.current },
  });

  const handleCopy = () => {
    void navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
  };

  if (!isVisible || closed) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        className={cn(
          DEBUG_DEV_PANEL_BASE_CLASS,
          "z-[99999] flex flex-col opacity-70",
        )}
        style={floatingStyles}
      >
        <div className="flex items-center justify-between gap-1 border-b border-amber px-2 py-1">
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              className="rounded p-0.5 text-amber hover:bg-amber hover:text-black"
              onClick={() => setExpandAll(!expandAll)}
              title={expandAll ? "Collapse all" : "Expand all"}
            >
              {expandAll ? (
                <Minimize2 className="size-3" />
              ) : (
                <Maximize2 className="size-3" />
              )}
            </button>
            <button
              type="button"
              className="rounded p-0.5 text-amber hover:bg-amber hover:text-black"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="size-3" />
              ) : (
                <Copy className="size-3" />
              )}
            </button>
            <button
              type="button"
              className="rounded p-0.5 text-amber hover:bg-amber hover:text-black"
              onClick={() => setClosed(true)}
            >
              <X className="size-3" />
            </button>
          </div>
        </div>
        <div className="max-h-[300px] max-w-[480px] overflow-auto no-scrollbar px-2.5 py-1.5 font-mono text-[10px] font-medium leading-relaxed text-black">
          {Object.entries(data).map(([key, value]) => (
            <div key={key}>
              <span className="font-bold">{key}:</span>{" "}
              <DebugJsonNode value={value} defaultExpanded={expandAll} />
            </div>
          ))}
        </div>
      </div>
    </FloatingPortal>
  );
};
